<template>
  <div style="display: flex">
    <UploadPhotoForm
      callerName="CustomersOwn"
      :img="[]"
      :showClose="false"
      :autoSelect="false"
      :showRemove="false"
      @uploadingStarted="$emit('uploadingStarted')"
      @imageUploadedToServer="onImageUploadedToServer"
      @uploadProgressChange="
        ({ isUploadInProgress }) => {
          setUploadProgress(isUploadInProgress);
        }
      "
      @error="$emit('error')"
      :isFilesLimitReached="isFilesLimitReached"
      style="width: 100%; height: 100%"
      :flow="flow"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { UploadedImage } from "@contracts/uploadedImage";
import UploadPhotoForm from "./UploadPhotoForm.vue";
import { inject } from "vue";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import { UploadFlow } from "@logic/ImageUploader";
import { PhotoGallerySource } from "@contracts/photosPageData";

export default defineComponent({
  name: "UploadPhotosNonAdventure",

  components: {
    UploadPhotoForm,
  },

  props: {
    customerId: { type: String as PropType<string | null>, required: false, default: null },
    autoCloseAfterUpload: { type: Boolean, default: true },
    isAddImagesToGallery: { type: Boolean, default: true },
    isFilesLimitReached: { type: Boolean, default: false, required: false },
    flow: { type: String as PropType<UploadFlow>, required: false, default: null },
    currentOperationSource: { type: String as PropType<PhotoGallerySource | null>, required: false, default: null },
  },

  emits: {
    imageAddedToGallery: (imageServerId: string) => true,
    uploadComplete: () => true,
    uploadProgressChange: (isUploadInProgress: boolean) => true,
    uploadingStarted: () => true,
    imageUploadedToServer: (uploadedImage: UploadedImage) => true,
    error: () => true,
  },

  data() {
    return {
      globalLog: inject("globalLog") as any,
      globalRemoteLogger: inject("globalRemoteLogger") as any,
      // Images that need saving to the gallery (they've already been uploaded)
      imagesToSave: [],
      isUploadingInProgress: false,

      checkForSaveInterval: null,
    };
  },

  watch: {
    isUploadingInProgress() {
      // If the uploading is done, then emit completion
      if (!this.isUploadingInProgress && this.autoCloseAfterUpload) {
        this.$emit("uploadComplete");
      }
    },
  },

  async mounted() {
    this.checkForSaveInterval = setInterval(() => {
      if (this.isAddImagesToGallery) {
        this.checkForSave();
      }
    }, 2000);
  },

  beforeUnmount() {
    clearInterval(this.checkForSaveInterval);
  },

  methods: {
    onImageUploadedToServer(image: UploadedImage) {
      this.$emit("imageUploadedToServer", image);
      // Note the it's dirty so we actually perform a save
      this.imagesToSave.push(image.serverId);

      if (this.isAddImagesToGallery) {
        // Immediately emit the event so the parent can update if needed
        this.$emit("imageAddedToGallery", image.serverId);
      }
    },
    async checkForSave() {
      // this.globalLog.info(`UploadPhotosNonAdventure checkForSave fired.`);
      if (this.imagesToSave?.length > 0 && this.customerId) {
        this.globalLog.info(`Adding ${this.imagesToSave.length} to the gallery`);
        let imagesToPersist = this.imagesToSave;

        try {
          this.imagesToSave = [];
          await PhotoGalleryV2Repo.upsertMyUploadsGroupPhotos(this.customerId, imagesToPersist, this.currentOperationSource);
          this.globalLog.info(`Added ${imagesToPersist.length} to the gallery`);

          // If the uploading is done, then emit completion
          if (!this.isUploadingInProgress && this.autoCloseAfterUpload) {
            this.$emit("uploadComplete");
          }
        } catch (e) {
          this.globalRemoteLogger.error(`PhotoGallery Save FAILED! Customer=${this.customerId}, AssetCount=${this.imagesToSave?.length}, Error=${e.message}`);
          // Put them back and try next time this fires
          this.imagesToSave.push(...imagesToPersist);
        }
      }
    },
    setUploadProgress(isUploadInProgress: boolean) {
      this.isUploadingInProgress = isUploadInProgress;
      this.$emit("uploadProgressChange", isUploadInProgress);
    },
  },
});
</script>
